import {
  BUSINESS_UNIT_LABELS,
  LOYALTY_AGING_BUCKET_LABELS,
  type LoyaltyLiabilityReport,
  type LoyaltySummary,
} from "@station/contracts";
import { Money, toIstanbulDate } from "@station/core";
import {
  Alert,
  Card,
  DateInput,
  FormField,
  MoneyText,
  Select,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companyLabel, useAccountingCompany } from "../../../features/accounting/company.js";
import { ScrollTable, Th } from "../../../features/customers/shared.js";
import {
  agingChartOption,
  agingRows,
  businessUnitRows,
  categoryBarOption,
  defaultSummaryRange,
  formatPoints,
  formatPointValue,
  liabilityStatus,
  SUMMARY_TOTAL_KEYS,
} from "../../../features/loyalty/loyalty-values.js";
import { useLiabilityReport, useLoyaltySummary } from "../../../features/loyalty/queries.js";
import {
  ChartPanel,
  LoyaltyPage,
  LoyaltySection as Section,
  Stat,
} from "../../../features/loyalty/shared.js";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatCount, formatDate, formatDateTime } from "../../../lib/format.js";

const TABS = ["liability", "summary"] as const;
type TabKey = (typeof TABS)[number];

const searchSchema = z.object({ tab: z.enum(TABS).optional().catch(undefined) });

export const Route = createFileRoute("/_app/sadakat/raporlar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ReportsRoute,
});

const thClass = "px-3 py-2 text-left";
const tdClass = "px-3 py-2";
const tdRight = "px-3 py-2 text-right";

/**
 * Sadakat raporları (ADR 0017 §24, #68, #69): **yükümlülük** — şirketin açık partileri × güncel puan değeri ↔ defterdeki
 * `LOYALTY_LIABILITY` bakiyesi ve fark (fark kaynakları sayfada yazılıdır), yaşlandırma, incelemedeki puan, eksi bakiyeler;
 * **dönem özeti** — kazanılan / harcanan / süresi dolan / geri alınan puan (toplam, istasyon, iş birimi), etkin üye ve
 * seviye dağılımı. Her grafiğin yanında aynı veriyi veren tablo vardır.
 */
function ReportsRoute(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const tab: TabKey = search.tab ?? "liability";
  return (
    <LoyaltyPage title={t("menu.loyaltyReports")} description={t("loyalty.reports.description")}>
      <Tabs
        value={tab}
        onValueChange={(next) =>
          void navigate({
            to: ".",
            search: ((previous: Record<string, unknown>) =>
              compactSearch({ ...previous, tab: next === "liability" ? undefined : next })) as never,
            replace: true,
          })
        }
      >
        <TabsList aria-label={t("loyalty.reports.tabsLabel")}>
          <TabsTrigger value="liability">{t("loyalty.reports.tabs.liability")}</TabsTrigger>
          <TabsTrigger value="summary">{t("loyalty.reports.tabs.summary")}</TabsTrigger>
        </TabsList>
        <TabsContent value="liability">{tab === "liability" ? <LiabilityTab /> : null}</TabsContent>
        <TabsContent value="summary">{tab === "summary" ? <SummaryTab /> : null}</TabsContent>
      </Tabs>
    </LoyaltyPage>
  );
}

// ── Yükümlülük ───────────────────────────────────────────────────────────────
function LiabilityTab(): ReactElement {
  const { t } = useTranslation();
  const accounting = useAccountingCompany();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [asOf, setAsOf] = useState<string>(toIstanbulDate(new Date()));
  const selected = companyId ?? accounting.companyId;
  const report = useLiabilityReport(selected, asOf === "" ? null : asOf);
  return (
    <div className="grid min-w-0 gap-4 [&>*]:min-w-0">
      <Card className="flex flex-wrap items-start gap-3 p-4">
        {accounting.companies.length > 1 ? (
          <FormField label={t("loyalty.reports.company")} className="w-72 max-w-full">
            {(control) => (
              <Select
                {...control}
                value={selected ?? ""}
                onChange={(event) => setCompanyId(event.target.value)}
              >
                {accounting.companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {companyLabel(company)}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        ) : null}
        <FormField
          label={t("loyalty.reports.asOf")}
          description={t("loyalty.reports.asOfHint")}
          className="w-44"
        >
          {(control) => (
            <DateInput {...control} value={asOf} onValueChange={(value) => setAsOf(value ?? "")} />
          )}
        </FormField>
      </Card>
      {report.error ? (
        <Alert tone="danger">{errorMessage(report.error)}</Alert>
      ) : !report.data ? (
        <SkeletonRows rows={8} />
      ) : (
        <LiabilityReportView report={report.data} />
      )}
    </div>
  );
}

function LiabilityReportView({ report }: { report: LoyaltyLiabilityReport }): ReactElement {
  const { t } = useTranslation();
  const status = liabilityStatus(report);
  const rows = agingRows(report);
  return (
    <>
      <Section
        title={t("loyalty.reports.liabilityTitle")}
        description={t("loyalty.reports.liabilityMeta", {
          value: formatPointValue(report.pointValue),
          at: formatDateTime(report.generatedAt),
        })}
      >
        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="liability-summary">
          <Stat
            label={t("loyalty.reports.outstanding")}
            hint={t("loyalty.pointsUnit", { points: formatPoints(report.outstanding.points) })}
          >
            <MoneyText value={report.outstanding.value} className="text-xl font-semibold" />
          </Stat>
          <Stat
            label={t("loyalty.reports.ledgerBalance")}
            hint={report.ledger ? `${report.ledger.accountCode} ${report.ledger.accountName}` : null}
          >
            {report.ledger ? (
              <MoneyText value={report.ledger.balance} className="text-xl font-semibold" />
            ) : (
              <span className="text-muted-foreground">{t("loyalty.reports.noLedger")}</span>
            )}
          </Stat>
          <Stat label={t("loyalty.reports.difference")}>
            {report.difference === null ? (
              "—"
            ) : (
              <span className="flex flex-wrap items-center gap-2">
                <MoneyText value={report.difference} colorize className="text-xl font-semibold" />
                <StatusBadge tone={status === "MATCHED" ? "success" : "warning"}>
                  {status === "MATCHED" ? t("loyalty.reports.matched") : t("loyalty.reports.different")}
                </StatusBadge>
              </span>
            )}
          </Stat>
          <Stat label={t("loyalty.reports.unclosed")} hint={t("loyalty.reports.unclosedHint")}>
            <MoneyText value={report.unclosedValue} />
          </Stat>
          <Stat
            label={t("loyalty.reports.pendingReview")}
            hint={t("loyalty.pointsUnit", { points: formatPoints(report.pendingReview.points) })}
          >
            <MoneyText value={report.pendingReview.value} />
          </Stat>
          <Stat
            label={t("loyalty.reports.negativeBalances")}
            hint={t("loyalty.reports.negativeHint", {
              count: formatCount(report.negativeBalances.accounts),
              points: formatPoints(report.negativeBalances.points),
            })}
          >
            <MoneyText value={report.negativeBalances.value} colorize />
          </Stat>
        </dl>
        {status === "DIFFERENT" ? (
          <Alert tone="warning" title={t("loyalty.reports.differenceTitle")}>
            {t("loyalty.reports.differenceSources")}
          </Alert>
        ) : null}
        {status === "NO_LEDGER" ? (
          <Alert tone="warning">
            {t("loyalty.reports.noLedgerHint")}{" "}
            <Link to="/muhasebe/eslestirmeler" className="underline underline-offset-4">
              {t("loyalty.reports.openMappings")}
            </Link>
          </Alert>
        ) : null}
        {report.warnings.length > 0 ? (
          <ul
            className="grid list-disc gap-1 pl-5 text-sm text-muted-foreground"
            aria-label={t("loyalty.reports.warnings")}
          >
            {report.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        ) : null}
      </Section>

      <Section title={t("loyalty.reports.agingTitle")} description={t("loyalty.reports.agingHint")}>
        <ChartPanel
          ariaLabel={t("loyalty.reports.agingChartAria")}
          option={agingChartOption(rows, {
            value: t("loyalty.reports.valueAxis"),
            points: t("loyalty.fields.points"),
            bucket: LOYALTY_AGING_BUCKET_LABELS,
          })}
          height={280}
          tableLabel={t("loyalty.reports.agingTable")}
        >
          <table className="w-full text-sm">
            <caption className="sr-only">{t("loyalty.reports.agingTable")}</caption>
            <thead className="bg-muted/50 text-xs text-muted-foreground">
              <tr>
                <th scope="col" className={thClass}>
                  {t("loyalty.reports.bucket")}
                </th>
                <th scope="col" className="px-3 py-2 text-right">
                  {t("loyalty.fields.points")}
                </th>
                <th scope="col" className="px-3 py-2 text-right">
                  {t("loyalty.fields.value")}
                </th>
                <th scope="col" className="px-3 py-2 text-right">
                  {t("loyalty.reports.share")}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.bucket} className="border-t">
                  <td className={tdClass}>{LOYALTY_AGING_BUCKET_LABELS[row.bucket]}</td>
                  <td className={`${tdRight} tabular`}>{formatPoints(row.points)}</td>
                  <td className={tdRight}>
                    <MoneyText value={row.value} />
                  </td>
                  <td className={`${tdRight} tabular`}>%{row.percent}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ChartPanel>
      </Section>

      <Section title={t("loyalty.reports.byStationTitle")}>
        {report.byStation.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("loyalty.reports.noOutstanding")}</p>
        ) : (
          <ScrollTable label={t("loyalty.reports.byStationTitle")}>
            <thead className="bg-muted/50 text-xs text-muted-foreground">
              <tr>
                <Th>{t("loyalty.fields.station")}</Th>
                <Th align="right">{t("loyalty.fields.points")}</Th>
                <Th align="right">{t("loyalty.fields.value")}</Th>
              </tr>
            </thead>
            <tbody>
              {report.byStation.map((row) => (
                <tr key={row.stationId} className="border-t">
                  <td className={tdClass}>{row.stationName}</td>
                  <td className={`${tdRight} tabular`}>{formatPoints(row.points)}</td>
                  <td className={tdRight}>
                    <MoneyText value={row.value} />
                  </td>
                </tr>
              ))}
            </tbody>
          </ScrollTable>
        )}
      </Section>
    </>
  );
}

// ── Dönem özeti ──────────────────────────────────────────────────────────────
function SummaryTab(): ReactElement {
  const { t } = useTranslation();
  const accounting = useAccountingCompany();
  const initial = defaultSummaryRange(toIstanbulDate(new Date()));
  const [from, setFrom] = useState<string>(initial.from);
  const [to, setTo] = useState<string>(initial.to);
  const [companyId, setCompanyId] = useState("");
  const valid = from !== "" && to !== "" && from <= to;
  const summary = useLoyaltySummary({
    ...(valid ? { from, to } : initial),
    companyId: companyId || undefined,
  });
  return (
    <div className="grid min-w-0 gap-4 [&>*]:min-w-0">
      <Card className="flex flex-wrap items-start gap-3 p-4">
        {accounting.companies.length > 1 ? (
          <FormField label={t("loyalty.reports.company")} className="w-72 max-w-full">
            {(control) => (
              <Select {...control} value={companyId} onChange={(event) => setCompanyId(event.target.value)}>
                <option value="">{t("loyalty.reports.allCompanies")}</option>
                {accounting.companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {companyLabel(company)}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        ) : null}
        <FormField label={t("loyalty.reports.from")} className="w-44">
          {(control) => (
            <DateInput {...control} value={from} onValueChange={(value) => setFrom(value ?? "")} />
          )}
        </FormField>
        <FormField
          label={t("loyalty.reports.to")}
          className="w-44"
          error={from !== "" && to !== "" && from > to ? t("loyalty.validation.dateOrder") : undefined}
        >
          {(control) => <DateInput {...control} value={to} onValueChange={(value) => setTo(value ?? "")} />}
        </FormField>
      </Card>
      {summary.error ? (
        <Alert tone="danger">{errorMessage(summary.error)}</Alert>
      ) : !summary.data ? (
        <SkeletonRows rows={8} />
      ) : (
        <SummaryView summary={summary.data} />
      )}
    </div>
  );
}

function SummaryView({ summary }: { summary: LoyaltySummary }): ReactElement {
  const { t } = useTranslation();
  const unitLabel = (unit: string | null) =>
    unit === null
      ? t("loyalty.reports.unknownUnit")
      : (BUSINESS_UNIT_LABELS[unit as keyof typeof BUSINESS_UNIT_LABELS] ?? unit);
  const units = businessUnitRows(summary, unitLabel);
  const tiers = summary.tierDistribution.map((row) => ({
    key: row.tierCode ?? "none",
    label: row.tierName ?? t("loyalty.reports.noTier"),
    value: row.accounts,
  }));
  return (
    <>
      <Section
        title={t("loyalty.reports.summaryTitle", {
          from: formatDate(summary.from),
          to: formatDate(summary.to),
        })}
      >
        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="loyalty-summary">
          {SUMMARY_TOTAL_KEYS.map((key) => (
            <Stat
              key={key}
              label={t(`loyalty.reports.totals.${key}`)}
              hint={<MoneyText value={summary.totals[key].value} colorize />}
            >
              <span className="tabular">
                {t("loyalty.pointsUnit", { points: formatPoints(summary.totals[key].points) })}
              </span>
            </Stat>
          ))}
          <Stat label={t("loyalty.reports.activeMembers")}>
            <span className="tabular">{formatCount(summary.activeMembers)}</span>
          </Stat>
        </dl>
      </Section>

      <div className="grid min-w-0 gap-4 xl:grid-cols-2 [&>*]:min-w-0">
        <Section title={t("loyalty.reports.byUnitTitle")}>
          {units.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("loyalty.reports.noEarn")}</p>
          ) : (
            <ChartPanel
              ariaLabel={t("loyalty.reports.byUnitChartAria")}
              option={categoryBarOption(units, t("loyalty.reports.earnedPoints"))}
              height={Math.max(220, units.length * 40 + 90)}
              tableLabel={t("loyalty.reports.byUnitTable")}
            >
              <table className="w-full text-sm">
                <caption className="sr-only">{t("loyalty.reports.byUnitTable")}</caption>
                <thead className="bg-muted/50 text-xs text-muted-foreground">
                  <tr>
                    <th scope="col" className={thClass}>
                      {t("loyalty.reports.businessUnit")}
                    </th>
                    <th scope="col" className="px-3 py-2 text-right">
                      {t("loyalty.reports.earnedPoints")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {units.map((row) => (
                    <tr key={row.key} className="border-t">
                      <td className={tdClass}>{row.label}</td>
                      <td className={`${tdRight} tabular`}>{formatPoints(row.points)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ChartPanel>
          )}
        </Section>
        <Section title={t("loyalty.reports.tiersTitle")}>
          {tiers.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("loyalty.reports.noMembers")}</p>
          ) : (
            <ChartPanel
              ariaLabel={t("loyalty.reports.tiersChartAria")}
              option={categoryBarOption(tiers, t("loyalty.reports.accounts"))}
              height={Math.max(220, tiers.length * 40 + 90)}
              tableLabel={t("loyalty.reports.tiersTable")}
            >
              <table className="w-full text-sm">
                <caption className="sr-only">{t("loyalty.reports.tiersTable")}</caption>
                <thead className="bg-muted/50 text-xs text-muted-foreground">
                  <tr>
                    <th scope="col" className={thClass}>
                      {t("loyalty.reports.tier")}
                    </th>
                    <th scope="col" className="px-3 py-2 text-right">
                      {t("loyalty.reports.accounts")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tiers.map((row) => (
                    <tr key={row.key} className="border-t">
                      <td className={tdClass}>{row.label}</td>
                      <td className={`${tdRight} tabular`}>{formatCount(row.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ChartPanel>
          )}
        </Section>
      </div>

      <Section title={t("loyalty.reports.byStationSummaryTitle")}>
        {summary.byStation.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("loyalty.reports.noMovements")}</p>
        ) : (
          <ScrollTable label={t("loyalty.reports.byStationSummaryTitle")}>
            <thead className="bg-muted/50 text-xs text-muted-foreground">
              <tr>
                <Th>{t("loyalty.fields.station")}</Th>
                {SUMMARY_TOTAL_KEYS.map((key) => (
                  <Th key={key} align="right">
                    {t(`loyalty.reports.totals.${key}`)}
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {summary.byStation.map((row) => (
                <tr key={`${row.companyId}:${row.stationId ?? "none"}`} className="border-t">
                  <td className={`${tdClass} whitespace-nowrap`}>
                    {row.stationName ?? t("loyalty.reports.noStation")}
                  </td>
                  {SUMMARY_TOTAL_KEYS.map((key) => (
                    <td key={key} className={`${tdRight} whitespace-nowrap`}>
                      <span className="grid justify-items-end">
                        <span className="tabular">{formatPoints(row[key].points)}</span>
                        <span className="text-xs text-muted-foreground">
                          {Money.fromMinor(row[key].value).formatTr()}
                        </span>
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </ScrollTable>
        )}
      </Section>
    </>
  );
}
